
import React from "react";
import { NavLink } from "react-router-dom";
import { i18nManager } from "../../../../i18n";
import { TriggerDataCleanup } from "./ManageData";
import MediaInput from "../../../shared/MediaInput";
import { Siren } from "../../../shared/ConfirmDialog";
import { CacheManager, Schemes, Utils } from "../../../../utils";
import { Debugger, Dropdown, FormControl, FormGroup, TextInput, useSubscription } from "@rivtn/noseur";
import { BaseService, ConsusService, CountryService, ICountry, IMember, IProfile, IUploadedFile, MemberService, SanitizedResponse } from "../../../../services";

export interface EditProfileProps {
    profile: IMember;
}

function EditProfile(props: EditProfileProps) {

    const consusService = ConsusService.getInstance();
    const memberService = MemberService.getInstance();
    const countryService = CountryService.getInstance();
    const [countries, setCountries] = React.useState<ICountry[]>();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [profile, setProfile] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.account.manage_account.edit_profile;
    const reportNewUpdateProfile = useSubscription("OMNEHOST.PROFILE.UPDATED");
    const profileWebsiteLink = Utils.buildPublicUrl("PROFILE", profile.username);
    const janusLaunariusProfile = CacheManager.get(CacheManager.PROFILE_KEY) as IProfile;

    React.useEffect(() => {
        memberService.getProfile().then(async ({ sanitized }: SanitizedResponse<IMember>) => {
            setProfile(sanitized);
            reportNewUpdateProfile(sanitized);
        }).catch((err: any) => {
            Debugger.log("ERROR WHEN FETCHING MEMBER PROFILE", err);
        });
        countryService.queryCountries({ name: "" }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
            setCountries(apiResponse.data!.content);
        }).catch(BaseService.reportError);
    }, []);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.manage_account.title} - {labels.title}</span>
        </div>
        <div className="edit-profile">
            <MediaInput instruction={i18nManager.Labels.common.click_to_choose} style={{ alignSelf: "center" }} width="100%" height={240} default={profile?.profile_banner_url}
                onSelectFiles={(files) => {
                    consusService.uploadFile("/omnehost/member/profile/", `pro_ban_${profile?.janus_user_external_id}`, files[0]).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        updateProfile({ ...profile, profile_banner_url: sanitized.consus_download_url });
                    }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                        setErrorMessage(errorMessage);
                    });
                }} onGenerateWithAi={async () => {
                    return new File(["We are here"], "we.txt", { type: 'text/plain' });
                }} onRemove={async () => {
                    updateProfile({ ...profile, profile_banner_url: "" });
                }} />
            <MediaInput instruction={i18nManager.Labels.common.choose} width={190} height={180} default={profile?.profile_picture_url ?? janusLaunariusProfile?.profile_picture_location}
                style={{ alignSelf: "center", marginTop: -90, zIndex: 1 }}
                onSelectFiles={async (files) => {
                    consusService.uploadFile("/omnehost/member/profile/", `pro_pic_${profile?.janus_user_external_id}`, files[0]).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        updateProfile({ ...profile, profile_picture_url: sanitized.consus_download_url })
                    }).catch(({ errorMessage }: SanitizedResponse<any>) => {
                        setErrorMessage(errorMessage);
                    });
                }} onGenerateWithAi={async () => {
                    return new File(["We are here"], "we.txt", { type: 'text/plain' });
                }} onRemove={async () => {
                    updateProfile({ ...profile, profile_picture_url: "" });
                }} />
            <FormGroup scheme={Schemes.OMNEHOST} className="form">
                <FormControl isFieldset fill label={labels.username}>
                    <TextInput defaultValue={profile.username} onInputComplete={(value) => updateProfile({ ...profile, username: value })} />
                </FormControl>
                <span className="purl">
                    {labels.profile_url}: <a href={profileWebsiteLink} rel="noreferrer" target="_blank">{profileWebsiteLink}</a>
                </span>
                <FormControl isFieldset fill label={labels.country_determinant}>
                    <Dropdown fill options={countries} optionMap={{ label: "name", value: "code" }} selectedOptionIndex={countries?.findIndex(c => c.code === profile.country_code)}
                        onSelectOption={(country: ICountry) => updateProfile({ ...profile, country_code: country.code })} />
                </FormControl>
                {errorMessage && (<div className="error">{errorMessage}</div>)}
                <hr style={errorMessage ? { marginTop: 20 } : {}} />
                <div className="twoed">
                    <FormControl isFieldset fill label={labels.email_address}>
                        <TextInput disabled defaultValue={profile?.user_detail?.email ?? janusLaunariusProfile.email} />
                    </FormControl>
                    <FormControl isFieldset fill label={labels.phone_number} style={{ flex: 0.7, marginLeft: 15 }}>
                        <TextInput disabled defaultValue={profile?.user_detail?.phone ?? janusLaunariusProfile.phone} />
                    </FormControl>
                </div>
                <div className="twoed">
                    <FormControl isFieldset fill label={labels.first_name}>
                        <TextInput disabled defaultValue={profile?.user_detail?.first_name ?? janusLaunariusProfile.first_name} />
                    </FormControl>
                    <FormControl isFieldset fill label={labels.last_name} style={{ marginLeft: 15 }}>
                        <TextInput disabled defaultValue={profile?.user_detail?.last_name ?? janusLaunariusProfile.last_name} />
                    </FormControl>
                </div>
                <a href={profileWebsiteLink} rel="noreferrer" target="_blank">{labels.update_email_name_on_rivtn}</a>
                <hr />
                <NavLink to="/account/manage-data" className={"link"}>{i18nManager.Labels.account.manage_account.manage_data.manage_your_data}</NavLink>
                <span className="link danger" onClick={() => TriggerDataCleanup("delete").show()}>{i18nManager.Labels.account.manage_account.manage_data.delete_omnehost_account}</span>
            </FormGroup>
        </div>
    </div>);

    function updateProfile(payload: IMember) {
        setErrorMessage("");
        memberService.upsertProfile(payload).then(({ sanitized }: SanitizedResponse<IMember>) => {
            setProfile(sanitized);
            props.profile = sanitized;
            Siren.alert(labels.update_success);
            reportNewUpdateProfile({ ...sanitized, profile_picture_url: sanitized.profile_picture_url + "?=" + (new Date()).toISOString() });
        }).catch(({ errorMessage }: SanitizedResponse<any>) => {
            setErrorMessage(errorMessage);
        });
    }

}

export default EditProfile;
